import { useRef } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { DataGridProProps } from "@mui/x-data-grid-pro";
import {
  Button,
  DataGrid,
  Download01Icon,
  LoadingOverlay,
  Typography,
  Upload01Icon,
} from "components";
import { colors } from "configs";
import { initLanContent } from "helpers";
import { useImportExcel } from "hooks";
import { i18n } from "translations";

import Upload from "./Upload";

interface ImportButtonProps {
  type: "employee" | "department";
  orgId: number;
}

const getDetailPanelContent: DataGridProProps["getDetailPanelContent"] = ({
  row,
}) => <Box sx={{ p: 2 }}>{String(row?.message ?? "")}</Box>;
const getDetailPanelHeight: DataGridProProps["getDetailPanelHeight"] = () => 50;

const ImportButton = (props: ImportButtonProps) => {
  const { type, orgId } = props;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    columns,
    rows,
    errorRows,
    modal,
    importState,
    enableImport,
    setModal,
    readExcel,
    handleSubmitImport,
    handleGetTemplate,
    handleGetErrorFile,
    handleReset,
  } = useImportExcel({
    type,
    orgId,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      readExcel(selectedFile);
    }
  };

  const handleReadFile = (file: File | null) => {
    handleReset();
    if (file) {
      readExcel(file);
    }
  };

  const handleOpenModal = () => {
    handleReset();
    setModal(true);
  };

  return (
    <div>
      <Stack direction="row" columnGap="8px">
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<Upload01Icon />}
          onClick={handleOpenModal}
          disabled={!orgId}
        >
          {i18n.t("import")}
        </Button>
      </Stack>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      <Dialog
        PaperProps={{
          sx: {
            overflow: "unset",
          },
        }}
        open={modal}
        maxWidth="lg"
      >
        <LoadingOverlay
          noPadding
          noBgColor
          loading={importState.loading}
          value={importState.progress}
        >
          <DialogTitle>Import</DialogTitle>
          <DialogContent>
            <Box width="700px">
              <Upload onChange={handleReadFile} fileExtensions={[".xlsx"]} />
              {!!rows?.length && !errorRows?.length && (
                <Box
                  width="100%"
                  mt="32px"
                  sx={{
                    "& .invalid-cell": {
                      backgroundColor: colors.error[200],
                    },
                  }}
                >
                  <Typography fontWeight={600} type="TextSM" color="grey.900">
                    Preview File
                  </Typography>
                  <DataGrid
                    containerHeight={400}
                    columns={columns}
                    rows={rows}
                    disableRowSelectionOnClick
                    getCellClassName={(params) => {
                      if (
                        (params.row?.invalidList as string[])?.includes(
                          params.field
                        )
                      )
                        return "invalid-cell";
                      return "";
                    }}
                  />
                </Box>
              )}
              {!!errorRows?.length && (
                <Box
                  width="100%"
                  mt="32px"
                  sx={{
                    "& .invalid-cell": {
                      backgroundColor: colors.error[200],
                    },
                  }}
                >
                  <Typography fontWeight={600} type="TextSM">
                    Error
                  </Typography>
                  <DataGrid
                    containerHeight={400}
                    columns={columns}
                    rows={errorRows}
                    disableRowSelectionOnClick
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={getDetailPanelHeight}
                    getCellClassName={(params) => {
                      if (
                        (params.row?.invalidList as string[])?.includes(
                          params.field
                        )
                      )
                        return "invalid-cell";
                      return "";
                    }}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Stack direction="row" gap={3}>
                {!importState.loading && !!errorRows?.length && (
                  <Button
                    variant="outlined"
                    onClick={handleGetErrorFile}
                    startIcon={<Download01Icon fontSize="small" />}
                  >
                    {initLanContent(["Xuất file lỗi", "Export Error file"])}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={handleGetTemplate}
                  startIcon={<Download01Icon fontSize="small" />}
                >
                  {initLanContent(["Tải xuống file mẫu", "Download template"])}
                </Button>
              </Stack>

              <Stack direction="row" gap={3}>
                {!importState.loading && (
                  <Button onClick={() => setModal(false)} variant="text">
                    {i18n.t("cancel")}
                  </Button>
                )}
                {!errorRows?.length && (
                  <Button
                    variant="contained"
                    onClick={handleSubmitImport}
                    startIcon={<Upload01Icon fontSize="small" />}
                    loading={importState.loading}
                    disabled={importState.loading || !enableImport}
                  >
                    {i18n.t("import")}
                  </Button>
                )}
              </Stack>
            </Stack>
          </DialogActions>
        </LoadingOverlay>
      </Dialog>
    </div>
  );
};

export default ImportButton;
